import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import styles from '../../styles/LoginForm.module.css';

const PasswordReset: React.FC = () => {
  const navigate = useNavigate();
  const notificationService = useNotification();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    if (!email.trim()) {
      setErrors({ email: 'Email is required' });
      setLoading(false);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrors({ email: 'Please enter a valid email address' });
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.requestPasswordReset(email);
      
      if (response.status === 'success') {
        setSubmitted(true);
        notificationService.success(response.message || 'Password reset email sent successfully');
      } else {
        setErrors({ general: response.message || 'Failed to send password reset email' });
        notificationService.error(response.message || 'Failed to send password reset email');
      }
    } catch (error: any) {
      console.error('Password reset error:', error);
      const errorMessage = error.response?.data?.message || 'An error occurred. Please try again later.';
      setErrors({ general: errorMessage });
      notificationService.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (errors[e.target.name]) {
      setErrors(prev => ({ ...prev, [e.target.name]: '' }));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.scrollContainer}>
        <div className={styles.contentContainer}>
          {/* Left Side - Illustration */}
          <div className={styles.illustrationContainer}>
            <div className={styles.spaceContent}>
              <div className={styles.logoContainer}>
                <img src="/images/wallet-transparent.png" alt="Wallet Logo" width="48" height="48" />
                <span className={styles.logoText}>Work Wallet</span>
              </div>
              <img src="/images/login-illustration.png" alt="Login Illustration" className={styles.illustration} />
              <div className={styles.featureList}>
                <div className={styles.featureItem}>
                  <div className={styles.featureIconContainer} style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)' }}>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="18" 
                      height="18" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      style={{ color: '#10b981' }}
                    >
                      <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                      <path d="m9 11 3 3L22 4"></path>
                    </svg>
                  </div>
                  <span className={styles.featureText}>Secure password reset</span>
                </div>
                <div className={styles.featureItem}>
                  <div className={styles.featureIconContainer} style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)' }}>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="18" 
                      height="18" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      style={{ color: '#3b82f6' }}
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </div>
                  <span className={styles.featureText}>Email verification</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form or Success Message */}
          <div className={styles.formWrapper}>
            <div className={styles.formBackground}>
              <div className={styles.formContainer}>
                {/* Mobile Header */}
                <div className={styles.mobileHeader}>
                  <div className={styles.mobileLogoContainer}>
                    <img src="/images/wallet-transparent.png" alt="Wallet Logo" width="36" height="36" />
                    <span className={styles.mobileLogoText}>Work Wallet</span>
                  </div>
                </div>

                {submitted ? (
                  <>
                    {/* Success State */}
                    <div className={styles.desktopHeader}>
                      <h1 className={styles.formTitle}>Check Your Email</h1>
                      <p className={styles.formSubtitle}>Password reset instructions have been sent</p>
                    </div>

                    <div style={{ 
                      padding: '1.5rem', 
                      backgroundColor: '#f0f9ff', 
                      borderRadius: '8px', 
                      marginBottom: '1.5rem',
                      border: '1px solid #bae6fd'
                    }}>
                      <p style={{ 
                        color: '#0369a1', 
                        margin: 0, 
                        lineHeight: '1.6',
                        fontSize: '0.9375rem'
                      }}>
                        If an account with the email <strong>{email}</strong> exists, 
                        you will receive a password reset email shortly. Please check your inbox 
                        and follow the instructions to reset your password.
                      </p>
                    </div>

                    <div style={{ 
                      padding: '1rem', 
                      backgroundColor: '#fef3c7', 
                      borderRadius: '8px', 
                      marginBottom: '1.5rem',
                      border: '1px solid #fde68a'
                    }}>
                      <p style={{ 
                        color: '#92400e', 
                        margin: 0, 
                        fontSize: '0.875rem',
                        lineHeight: '1.5'
                      }}>
                        <strong>Note:</strong> If you don't see the email, check your spam folder. 
                        The email may take a few minutes to arrive.
                      </p>
                    </div>

                    <button 
                      type="button"
                      onClick={() => navigate('/auth/signin')}
                      className={styles.submitButton}
                    >
                      <span className={styles.submitButtonText}>Back to Sign In</span>
                    </button>

                    <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                      <Link 
                        to="/auth/signin" 
                        style={{ 
                          color: '#3b82f6', 
                          textDecoration: 'none',
                          fontSize: '0.875rem'
                        }}
                      >
                        ← Return to login
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Form State */}
                    <div className={styles.desktopHeader}>
                      <h1 className={styles.formTitle}>Reset Password</h1>
                      <p className={styles.formSubtitle}>Enter your email to receive reset instructions</p>
                    </div>

                    {/* Error Messages */}
                    {errors.general && (
                      <div className={styles.formErrorContainer}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#b91c1c" className="bi bi-exclamation-triangle" viewBox="0 0 16 16">
                          <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.15.15 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.2.2 0 0 1-.054.06.1.1 0 0 1-.066.017H1.146a.1.1 0 0 1-.066-.017.2.2 0 0 1-.054-.06.18.18 0 0 1 .002-.183L7.884 2.073a.15.15 0 0 1 .054-.057m1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767z"/>
                          <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z"/>
                        </svg>
                        <span className={styles.formErrorText} style={{lineHeight:'1'}}>{errors.general}</span>
                      </div>
                    )}

                    {/* Password Reset Form */}
                    <form onSubmit={handleSubmit}>
                      {/* Email Field */}
                      <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>Institutional Email</label>
                        <div 
                          className={`${styles.inputContainer} ${errors.email ? styles.inputErrorState : ''}`}
                        >
                          <svg style={{ color: '#687076',marginRight: '12px' }} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-lucide="mail" className="lucide lucide-mail">
                            <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path>
                            <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                          </svg>
                          
                          <input 
                            type="email" 
                            name="email" 
                            className={styles.input} 
                            placeholder="Enter your institutional email" 
                            value={email}
                            onChange={handleChange}
                            autoComplete="email"
                            required 
                          />
                        </div>
                        {errors.email && (
                          <div className={styles.errorContainer} style={{ display: 'flex' }}>
                            <svg style={{ color: '#ef4444', width: '14px', height: '14px' }} fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <span className={styles.errorText}>{errors.email}</span>
                          </div>
                        )}
                      </div>

                      {/* Submit Button or Loading State */}
                      {loading ? (
                        <div className={styles.loadingState} style={{ display: 'flex' }}>
                          <div className={styles.spinner}></div>
                          <span>Sending reset email...</span>
                        </div>
                      ) : (
                        <button 
                          type="submit" 
                          className={styles.submitButton}
                        >
                          <span className={styles.submitButtonText}>Send Reset Email</span>
                        </button>
                      )}
                    </form>

                    {/* Back to Login Link */}
                    <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                      <Link 
                        to="/auth/signin" 
                        style={{ 
                          color: '#3b82f6', 
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        Back to Sign In
                      </Link>
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              <div className={styles.footer}>
                <p className={styles.footerText}>© 2025 Work Wallet - Faculty Recognition System</p>
                <p className={styles.footerLink}>Need help? <a className={styles.footerLinkText}>Contact support</a></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;

